import { capitalize } from "@mui/material";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { classes, STATUS_TEXT } from "portal/theme/theme";
import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateTime } from "luxon";
import {
  GridActionsColDef,
  GridColDef,
  GridToolbarExport,
} from "@mui/x-data-grid-premium";
import { Measurement } from "portal/components/measurement/Measurement";
import { portalApi } from "portal/state/portalApi";
import { SearchField } from "portal/components/header/SearchField";
import { skipToken } from "@reduxjs/toolkit/query";
import { titleCase } from "common/utils/strings";
import { useFuzzySearch } from "portal/utils/hooks/useFuzzySearch";
import { useJigsawMetrics } from "../../utils/hooks/useJigsawMetrics";
import { useParams } from "react-router-dom";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { valueOrLoading } from "portal/utils/strings";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import React, { FunctionComponent } from "react";

const defaultColumn: Partial<GridColDef> = {
  sortable: true,
  disableColumnMenu: true,
};

interface ModuleDefinition {
  rowId: number;
  moduleId: number;
  moduleSerial: string | undefined;
  disabled: boolean;
  moduleSpacingMm: number;
  rowSpacingMm: number;
}

const _RobotWeedingModulesList: FunctionComponent = () => {
  const { serial } = useParams();
  const { i18n } = useTranslation();
  const useJigsaw = useJigsawMetrics();

  const {
    data: reaperConfig,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useQueryPopups(
    portalApi.endpoints.getReaperConfiguration.useQuery(
      serial ? { serial } : skipToken
    )
  );

  const { data: summary } = useQueryPopups(
    portalApi.endpoints.getRobot.useQuery(
      serial ? { language: i18n.language, serial, useJigsaw } : skipToken
    )
  );

  const { t } = useTranslation();

  const resolvedReaperConfig =
    reaperConfig?.currentRobotDefinition?.rows.flatMap((row) => {
      return row.modules.map((module) => {
        const moduleConf: ModuleDefinition = {
          rowId: row.rowId,
          moduleId: module.moduleId,
          moduleSerial: reaperConfig.assignedModules.find(
            (am) => am.id === module.moduleId
          )?.serial,
          disabled: module.disabled,
          moduleSpacingMm: module.moduleSpacingMm,
          rowSpacingMm: row.rowSpacingMm,
        };
        return moduleConf;
      });
    }) || [];

  const { searchText, setSearchText, results } =
    useFuzzySearch<ModuleDefinition>({
      items: resolvedReaperConfig,
      options: {
        keys: ["rowNumber", "moduleId", "moduleSerial"],
      },
    });

  const columns: Array<GridColDef<ModuleDefinition> | GridActionsColDef> = [
    {
      ...defaultColumn,
      field: "rowId",
      headerName: titleCase(t("models.modules.fields.rowNumber")),
      cellClassName: "font-mono",
      sortComparator: new Intl.Collator(undefined, { numeric: true }).compare,
      width: 75,
    },
    {
      ...defaultColumn,
      field: "moduleId",
      headerName: titleCase(t("models.modules.module_one")),
      cellClassName: "font-mono",
      sortComparator: new Intl.Collator(undefined, { numeric: true }).compare,
      width: 100,
    },
    {
      ...defaultColumn,
      field: "moduleSerial",
      headerName: t("models.modules.fields.serial.value"),
      cellClassName: ({ value }) =>
        classes({ "font-mono": value, [STATUS_TEXT.GRAY]: !value }),
      width: 150,
      valueFormatter: (value) =>
        (value as ModuleDefinition["moduleSerial"]) ||
        t("models.modules.fields.serial.unassigned"),
    },
    {
      ...defaultColumn,
      field: "disabled",
      headerName: t("models.modules.fields.isEnabled"),
      cellClassName: "font-mono",
      width: 125,
      valueFormatter: (value) =>
        (value as ModuleDefinition["disabled"])
          ? t("utils.descriptors.disabled")
          : t("utils.descriptors.enabled"),
    },
    {
      ...defaultColumn,
      field: "moduleSpacingMm",
      headerName: t("models.modules.fields.moduleSpacingMm"),
      width: 175,
      cellClassName: "font-mono",
      renderCell: ({ value }) => <Measurement value={value} />,
    },
    {
      ...defaultColumn,
      field: "rowSpacingMm",
      headerName: t("models.modules.fields.rowSpacingMm"),
      width: 175,
      cellClassName: "font-mono",
      renderCell: ({ value }) => <Measurement value={value} />,
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {isConfigError ? (
        <span>{t("components.ModuleTable.modulesUnavailable")}</span>
      ) : (
        <CarbonDataGrid<ModuleDefinition>
          getRowId={(row) => row.moduleId}
          header={
            <>
              <SearchField
                value={searchText}
                onChange={setSearchText}
                label={t("utils.actions.searchLong", {
                  subject: capitalize(
                    t("models.modules.module", {
                      count: resolvedReaperConfig.length,
                    })
                  ),
                })}
              />
              <div className="flex items-center">
                <GridToolbarExport
                  csvOptions={{
                    fileName: t("components.ModuleTable.export", {
                      robots: valueOrLoading(summary?.robot?.customerSerial, t),
                      date: DateTime.local().toFormat(DATE_PATH_FORMAT),
                    }),
                  }}
                  excelOptions={{ disableToolbarButton: true }}
                  printOptions={{ disableToolbarButton: true }}
                />
              </div>
            </>
          }
          className={classes("flex flex-1")}
          loading={isConfigLoading || !reaperConfig}
          rows={results}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [
                { field: "rowNumber", sort: "asc" },
                { field: "moduleId", sort: "asc" },
              ],
            },
          }}
          hideFooter
          disableRowSelectionOnClick
        />
      )}
    </div>
  );
};

export const RobotWeedingModulesList = withAuthenticationRequired(
  _RobotWeedingModulesList
);
